import {
  ChunkLoadPayload,
  ChunkUnloadPayload,
  DialogueContentPayload,
  EmoticonPayload,
  EntityDeltaPayload,
  EntityDestroyedPayload,
  EntityLeftAOIPayload,
  EntityStatePayload,
  GameTimeSyncPayload,
  HotReloadPayload,
  MapSyncPayload,
  SceneMetadataPayload,
  SceneSyncPayload,
  ServerPausedPayload,
  WhoAmIPayload,
  WorldSyncPayload,
} from '@common/api';
import { InteractingWithData, LightSourceData, TransformData } from '@common/components/components';
import { InteractionStoppedEvent } from '@common/events';

import { ClientServiceContainer } from './ClientServiceContainer';

/** Player visibility halo settings (low strength, small radius, local only) */
const PLAYER_VISIBILITY_HALO: LightSourceData = {
  strength: 4,
  distance: 48, // 48 pixels radius
  color: 0xffffff,
  colorAlpha: 1,
  localOnly: true,
};

export class ClientNetworkEventHandler {
  private services: ClientServiceContainer;
  private readonly RECONCILIATION_THRESHOLD_PX = 8.0;

  constructor(services: ClientServiceContainer) {
    this.services = services;
  }

  public registerHandlers(): void {
    const { messageDispatch } = this.services;

    messageDispatch.registerHandler('whoAmI', this.handleWhoAmI.bind(this));
    messageDispatch.registerHandler('worldSync', this.handleWorldSync.bind(this));
    messageDispatch.registerHandler('entityState', this.handleEntityState.bind(this));
    messageDispatch.registerHandler('entityDelta', this.handleEntityDelta.bind(this));
    messageDispatch.registerHandler('entityLeftAOI', this.handleEntityLeftAOI.bind(this));
    messageDispatch.registerHandler('entityDestroyed', this.handleEntityDestroyed.bind(this));
    messageDispatch.registerHandler('mapSync', this.handleMapSync.bind(this));
    messageDispatch.registerHandler('sceneSync', this.handleSceneSync.bind(this));
    messageDispatch.registerHandler('sceneMetadata', this.handleSceneMetadata.bind(this));
    messageDispatch.registerHandler('chunkLoad', this.handleChunkLoad.bind(this));
    messageDispatch.registerHandler('chunkUnload', this.handleChunkUnload.bind(this));
    messageDispatch.registerHandler('gameTimeSync', this.handleGameTimeSync.bind(this));
    messageDispatch.registerHandler('dialogueContent', this.handleDialogueContent.bind(this));
    messageDispatch.registerHandler('dialogueEnded', this.handleDialogueEnded.bind(this));
    messageDispatch.registerHandler('serverPaused', this.handleServerPaused.bind(this));
    messageDispatch.registerHandler('heartbeat', this.handleHeartbeat.bind(this));
    messageDispatch.registerHandler('emoticon', this.handleEmoticon.bind(this));
    messageDispatch.registerHandler('hotReload', this.handleHotReload.bind(this));
  }

  private handleHeartbeat(): void {
    // No-op: Heartbeat is already used for connection health detection in main.ts
    // Registering this handler simply silences "Unknown payload type" warnings.
  }

  private handleWhoAmI(payload: WhoAmIPayload): void {
    const { clientWorld, localPlayer } = this.services;
    const clientEntityId = clientWorld.getWorld().getClientEntityId(payload.data.entityId);
    localPlayer.handleWhoAmI(payload.data, clientEntityId);
  }

  private handleWorldSync(payload: WorldSyncPayload): void {
    const { clientWorld, render, pixiInstance, localPlayer } = this.services;
    pixiInstance.clear();
    clientWorld.syncWorld(payload.data, render);

    // After a full world sync (e.g. on reload), we must re-evaluate our local entity ID.
    // If we don't, position synchronization systems will skip the local player.
    if (localPlayer.serverEntityId !== undefined) {
      const clientEntityId = clientWorld.getWorld().getClientEntityId(localPlayer.serverEntityId);
      if (clientEntityId !== undefined) {
        localPlayer.entityId = clientEntityId;
        console.log(
          `[ClientNetworkEventHandler] Restored local entity mapping: server=${localPlayer.serverEntityId} -> client=${clientEntityId}`,
        );
      }
    }
  }

  /**
   * Handle full entity state (create, AOI enter, sync).
   */
  private handleEntityState(payload: EntityStatePayload): void {
    const { clientWorld, localPlayer } = this.services;
    const clientEntityId = clientWorld.createNetworkedEntity(payload.entityId, payload.components);

    // Check if this entity is the local player
    if (localPlayer.serverEntityId === payload.entityId) {
      localPlayer.entityId = clientEntityId;
      this.addPlayerVisibilityHalo(clientEntityId);
      this.services.inventoryUI.notifyRefresh();
    }
  }

  /**
   * Handle incremental component changes (add, update, remove).
   */
  private handleEntityDelta(payload: EntityDeltaPayload): void {
    const { clientWorld, localPlayer } = this.services;

    // Handle component additions
    if (payload.add) {
      for (const [componentName, data] of Object.entries(payload.add)) {
        clientWorld.addComponent(payload.entityId, componentName, data);
      }
    }

    // Handle component updates
    if (payload.update) {
      for (const [componentName, data] of Object.entries(payload.update)) {
        // Skip updates for local player's predicted components
        if (payload.entityId === localPlayer.serverEntityId && localPlayer.entityId !== undefined) {
          // Facing: always trust client's local prediction (intent-based)
          if (componentName === 'Facing') {
            continue;
          }

          // Transform: loose reconciliation - only snap if large discrepancy
          if (componentName === 'Transform') {
            const currentTransform = clientWorld
              .getWorld()
              .getComponent(localPlayer.entityId, 'Transform');
            const newTransform = data as TransformData;

            if (currentTransform) {
              const dx = currentTransform.x - newTransform.x;
              const dy = currentTransform.y - newTransform.y;
              const distSq = dx * dx + dy * dy;

              if (distSq < this.RECONCILIATION_THRESHOLD_PX * this.RECONCILIATION_THRESHOLD_PX) {
                continue;
              }
            }
          }
        }

        clientWorld.updateComponent(payload.entityId, componentName, data);

        // Notify inventory UI when an Inventory or Equipment component is updated
        if (componentName === 'Inventory' || componentName === 'Equipment') {
          this.services.inventoryUI.notifyRefresh();
        }
      }
    }

    // Handle component removals
    if (payload.remove) {
      for (const componentName of payload.remove) {
        // [MODIFIED] Fire InteractionStopped event if InteractingWith is removed from local player
        if (payload.entityId === localPlayer.serverEntityId && localPlayer.entityId !== undefined) {
          if (componentName === 'InteractingWith') {
            const interactingWith = clientWorld
              .getWorld()
              .getComponent(localPlayer.entityId, 'InteractingWith') as
              | InteractingWithData
              | undefined;

            if (interactingWith) {
              this.services.common.eventBus.addDeferredEvent({
                name: 'InteractionStopped',
                source: 'ClientNetworkEventHandler',
                payload: {
                  entityId: localPlayer.entityId,
                  interactionType: interactingWith.type,
                },
              } as InteractionStoppedEvent);
            }
          }
        }

        clientWorld.removeComponent(payload.entityId, componentName);
      }
    }
  }

  private handleEntityLeftAOI(payload: EntityLeftAOIPayload): void {
    const { clientWorld } = this.services;
    clientWorld.destroyEntity(payload.entityId);
  }

  private handleEntityDestroyed(payload: EntityDestroyedPayload): void {
    this.services.clientWorld.destroyEntity(payload.entityId);
  }

  private handleMapSync(payload: MapSyncPayload): void {
    this.services.common.mapData.setMap(payload.mapId, payload.data);
  }

  private handleSceneSync(payload: SceneSyncPayload): void {
    this.services.common.mapData.setScene(payload.sceneId, payload.mapId);
    this.services.common.eventBus.addDeferredEvent({
      name: 'MapReloadRequested',
      source: 'ClientNetworkEventHandler',
      payload: { sceneId: payload.sceneId },
    });
  }

  private handleSceneMetadata(payload: SceneMetadataPayload): void {
    const { common, chunkData } = this.services;
    common.mapData.setScene(payload.sceneId, payload.mapId);
    const metadata = {
      tileWidth: payload.tileWidth,
      tileHeight: payload.tileHeight,
      minChunkX: payload.minChunkX,
      maxChunkX: payload.maxChunkX,
      minChunkY: payload.minChunkY,
      maxChunkY: payload.maxChunkY,
      chunkSize: payload.chunkSize,
      tilesets: payload.tilesets,
      layerGroups: payload.layerGroups,
    };
    chunkData.sceneMetadata[payload.sceneId] = metadata;
  }

  private handleChunkLoad(payload: ChunkLoadPayload): void {
    const { chunkData, common } = this.services;
    chunkData.handleChunkLoad(payload.sceneId, payload.chunkX, payload.chunkY, payload.data);
    common.eventBus.addDeferredEvent({
      name: 'ChunkLoaded',
      source: 'handleChunkLoad',
      payload: {
        sceneId: payload.sceneId,
        chunkX: payload.chunkX,
        chunkY: payload.chunkY,
      },
    });
  }

  private handleChunkUnload(payload: ChunkUnloadPayload): void {
    const { chunkData, common } = this.services;
    chunkData.handleChunkUnload(payload.sceneId, payload.chunkX, payload.chunkY);
    common.eventBus.addDeferredEvent({
      name: 'ChunkUnloaded',
      source: 'handleChunkUnload',
      payload: {
        sceneId: payload.sceneId,
        chunkX: payload.chunkX,
        chunkY: payload.chunkY,
      },
    });
  }

  private handleGameTimeSync(payload: GameTimeSyncPayload): void {
    this.services.common.gameTime.setDateTime({
      year: payload.year,
      month: payload.month,
      day: payload.day,
      hour: payload.hour,
      minute: payload.minute,
    });
  }

  private handleDialogueContent(payload: DialogueContentPayload): void {
    this.services.dialogue.handleContent(payload);
  }

  private handleDialogueEnded(): void {
    this.services.dialogue.handleEnded();
  }

  private handleServerPaused(payload: ServerPausedPayload): void {
    this.services.connectionState.onServerPaused(payload.isPaused);
  }

  /**
   * Add a visibility halo LightSource to the local player entity.
   * This allows the player to see a small area around them even in complete darkness.
   */
  private addPlayerVisibilityHalo(clientEntityId: number): void {
    const world = this.services.clientWorld.getWorld();

    // Only add if entity doesn't already have a LightSource
    const existingLight = world.getComponent(clientEntityId, 'LightSource');
    if (existingLight) {
      return;
    }

    const lightSource = world.createComponent('LightSource', PLAYER_VISIBILITY_HALO);
    world.addComponent(clientEntityId, lightSource);

    // Initialize camera to follow this player
    this.initializeCameraEntity(clientEntityId);
  }

  /**
   * Create the camera entity and set it to follow the player.
   */
  private initializeCameraEntity(targetEntityId: number): void {
    const { camera, clientWorld } = this.services;
    const world = clientWorld.getWorld();

    // Don't recreate if already initialized
    if (camera.cameraEntityId !== undefined) {
      // Update the existing camera's follow target
      const cameraFollow = world.getComponent(camera.cameraEntityId, 'CameraFollow');
      if (cameraFollow) {
        cameraFollow.targetEntityId = targetEntityId;
      }
      return;
    }

    // Get the player's initial transform to set camera scene
    const playerTransform = world.getComponent(targetEntityId, 'Transform');
    const initialSceneId = playerTransform?.sceneId ?? 'overworld';

    // Create camera entity
    const cameraId = world.createEntity();

    const cameraTransform = world.createComponent('Transform', {
      x: playerTransform?.x ?? 0,
      y: playerTransform?.y ?? 0,
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      sceneId: initialSceneId,
    });
    world.addComponent(cameraId, cameraTransform);

    const cameraFollow = world.createComponent('CameraFollow', {
      targetEntityId,
    });
    world.addComponent(cameraId, cameraFollow);

    camera.cameraEntityId = cameraId;
  }
  private handleEmoticon(payload: EmoticonPayload): void {
    const { common, clientWorld } = this.services;
    const clientEntityId = clientWorld.getWorld().getClientEntityId(payload.entityId);

    if (clientEntityId === undefined) {
      return;
    }

    common.eventBus.addDeferredEvent({
      name: 'ShowEmoticon',
      source: 'handleEmoticon',
      payload: {
        entityId: clientEntityId,
        emoticonId: payload.emoticonId,
      },
    });
  }

  private handleHotReload(payload: HotReloadPayload): void {
    const { common, spritesheet } = this.services;

    // 1. Clear SpritesheetService cache
    spritesheet.clearCache(payload.timestamp);

    // 2. Fire global event so systems (like SpriteLoadingSystem) can reset
    common.eventBus.addDeferredEvent({
      name: 'HotReload',
      source: 'ClientNetworkEventHandler',
      payload: {
        timestamp: payload.timestamp,
      },
    });

    console.log(
      `[ClientNetworkEventHandler] Global hot-reload triggered with timestamp: ${payload.timestamp}`,
    );
  }
}
